"use client";
import { QueryClient } from '@tanstack/query-core';
import { serialize } from 'superjson';
import { isClient, isNotInUserTestEnvironment, isServer } from 'blitz';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const requestIdleCallback = typeof self !== "undefined" && self.requestIdleCallback && self.requestIdleCallback.bind(window) || function(cb) {
  let start = Date.now();
  return setTimeout(function() {
    cb({
      didTimeout: false,
      timeRemaining: function() {
        return Math.max(0, 50 - (Date.now() - start));
      }
    });
  }, 1);
};
const initializeQueryClient = () => {
  let suspenseEnabled = true;
  if (!process.env.CLI_COMMAND_CONSOLE && !process.env.CLI_COMMAND_DB) {
    suspenseEnabled = Boolean(globalThis.__BLITZ_SUSPENSE_ENABLED);
  }
  return new QueryClient({
    defaultOptions: {
      queries: __spreadProps(__spreadValues({}, isServer && { cacheTime: 0 }), {
        suspense: suspenseEnabled,
        retry: (failureCount, error) => {
          if (process.env.NODE_ENV !== "production")
            return false;
          if (error.message === "Network request failed" && failureCount <= 3)
            return true;
          return false;
        }
      })
    }
  });
};
const getQueryClient = () => globalThis.queryClient;
function isRpcClient(f) {
  return !!f._isRpcClient;
}
const getQueryCacheFunctions = (resolver, params) => ({
  setQueryData: (newData, opts = { refetch: true }) => {
    return setQueryData(resolver, params, newData, opts);
  }
});
const emptyQueryFn = (() => {
  const fn = () => new Promise(() => {
  });
  fn._isRpcClient = true;
  return fn;
})();
const validateQueryFn = (queryFn) => {
  if (isClient && !isRpcClient(queryFn) && isNotInUserTestEnvironment()) {
    throw new Error(
      `Either the file path to your resolver is incorrect (must be in a "queries" or "mutations" folder that isn't nested inside "pages" or "api") or you are trying to use Blitz's useQuery to fetch from third-party APIs (to do that, import useQuery directly from "@tanstack/react-query").`
    );
  }
};
const sanitize = (type) => (queryFn) => {
  if (isServer)
    return queryFn;
  validateQueryFn(queryFn);
  const rpcClient = queryFn;
  const queryFnName = type === "mutation" ? "useMutation" : "useQuery";
  if (rpcClient._resolverType !== type && isNotInUserTestEnvironment()) {
    throw new Error(
      `"${queryFnName}" was expected to be called with a ${type} but was called with a "${rpcClient._resolverType}"`
    );
  }
  return rpcClient;
};
const sanitizeQuery = sanitize("query");
const sanitizeMutation = sanitize("mutation");
const getQueryKeyFromUrlAndParams = (url, ...params) => {
  const queryKey = [url];
  if (params.length === 1) {
    const param = params[0];
    queryKey.push(serialize(typeof param === "function" ? param() : param));
  }
  return queryKey;
};
function getQueryKey(resolver, ...params) {
  if (typeof resolver === "undefined") {
    throw new Error("getQueryKey is missing the first argument - it must be a resolver function");
  }
  return getQueryKeyFromUrlAndParams(sanitizeQuery(resolver)._routePath, ...params);
}
function getInfiniteQueryKey(resolver, params) {
  if (typeof resolver === "undefined") {
    throw new Error(
      "getInfiniteQueryKey is missing the first argument - it must be a resolver function"
    );
  }
  const queryKey = getQueryKeyFromUrlAndParams(sanitizeQuery(resolver)._routePath, params);
  return [...queryKey, "infinite"];
}
const invalidateQuery = (resolver = void 0, ...params) => {
  const fullQueryKey = typeof resolver === "undefined" ? void 0 : getQueryKey(resolver, ...params);
  return getQueryClient().invalidateQueries(fullQueryKey);
};
function setQueryData(resolver, params, newData, opts = { refetch: true }) {
  if (typeof resolver === "undefined") {
    throw new Error("setQueryData is missing the first argument - it must be a resolver function");
  }
  const queryKey = getQueryKey(resolver, params);
  return new Promise((res) => {
    getQueryClient().setQueryData(queryKey, newData);
    let result = void 0;
    if (opts.refetch) {
      result = invalidateQuery(resolver, params);
    }
    if (isClient) {
      requestIdleCallback(() => {
        res(result);
      });
    } else {
      res(result);
    }
  });
}
function getQueryData(resolver, params) {
  if (typeof resolver === "undefined") {
    throw new Error("getQueryData is missing the first argument - it must be a resolver function");
  }
  const queryKey = getQueryKey(resolver, params);
  return getQueryClient().getQueryData(queryKey);
}

export { emptyQueryFn, getInfiniteQueryKey, getQueryCacheFunctions, getQueryClient, getQueryData, getQueryKey, getQueryKeyFromUrlAndParams, initializeQueryClient, invalidateQuery, requestIdleCallback, sanitizeMutation, sanitizeQuery, setQueryData, validateQueryFn };
